import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { createExpense } from '../api';
import { useToast } from '../../../context/ToastContext';
import { CategoriaConceptoSelect, useCatalogo } from './ConceptoSelect';
import { FinSection } from '../api';

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';

/**
 * Modal para registrar un gasto. onCreated(month) refresca la vista.
 * `secciones`: de qué apartado es (Gastos = egreso, Inversiones, Nómina › otros gastos de personal); el
 * formulario solo ofrece las categorías de ese apartado (Mabel 7-oct-2026: cada apartado en su pestaña).
 */
export default function ExpenseForm({ onClose, onCreated, secciones = ['egreso'], titulo = 'Registrar gasto' }: {
  onClose: () => void; onCreated: (month: string) => void; secciones?: FinSection[]; titulo?: string;
}) {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayLocal());
  // Catálogo (7-oct-2026, Mabel): Apartado › Categoría › Concepto + IVA opcional (el monto es lo pagado).
  const [conceptId, setConceptId] = useState<number | null>(null);
  const [conIva, setConIva] = useState(false);
  const [ivaPct, setIvaPct] = useState('');
  const { ivaDefault, gastoFijo, catalogo } = useCatalogo();
  // Fijo / Variable (Mabel 7-oct-2026: «fijo solo es la renta»): se propone solo por el concepto (Renta → Fijo)
  // hasta que lo toquen a mano. Solo en Egresos y solo si la API ya lo guarda (`gastoFijo`).
  const [fijo, setFijo] = useState<boolean | null>(null);
  const conFijo = gastoFijo && secciones.includes('egreso');
  const esRenta = !!conceptId && (catalogo || []).some((s) => s.categorias.some((c) => c.conceptos.some((k) => k.concept_id === conceptId && k.system_key === 'renta')));
  const fijoEfectivo = fijo ?? esRenta;
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error('Indica un monto válido'); return; }
    if (!conceptId) { toast.error('Elige el concepto del gasto'); return; }
    const pct = conIva ? Number(ivaPct || ivaDefault) : null;
    setSaving(true);
    try {
      const r = await createExpense({ amount: amt, date, concept_id: conceptId, iva_pct: pct, notes: notes || null,
        ...(conFijo ? { is_fixed: fijoEfectivo } : {}) });
      toast.success('Gasto registrado');
      onCreated(r.month);
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo registrar el gasto');
    } finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{titulo}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Importe (MXN)</label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={inputCls} autoFocus />
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
          </div>

          <CategoriaConceptoSelect value={conceptId} onChange={setConceptId} secciones={secciones} />

          {conFijo && (
            <div>
              <label className={labelCls}>Tipo de gasto</label>
              <div className="inline-flex rounded-lg border border-line bg-surface p-0.5">
                {([[true, 'Fijo'], [false, 'Variable']] as const).map(([v, label]) => (
                  <button key={label} type="button" onClick={() => setFijo(v)}
                    className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${fijoEfectivo === v ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="inline-flex items-center gap-2 text-sm text-content">
              <input type="checkbox" checked={conIva} onChange={(e) => setConIva(e.target.checked)} /> Lleva IVA (opcional)
            </label>
            {conIva && (
              <div className="mt-2 flex items-center gap-2 text-sm text-content-2">
                <input type="number" min={1} max={30} step="0.01" value={ivaPct || ivaDefault} onChange={(e) => setIvaPct(e.target.value)}
                  className="w-20 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
                <span>%</span>
                {Number(amount) > 0 && (
                  <span className="text-xs text-content-3">
                    IVA incluido: ${(Number(amount) * Number(ivaPct || ivaDefault) / (100 + Number(ivaPct || ivaDefault))).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                  </span>
                )}
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Observaciones (opcional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder="Ej. factura 1234, pagado en efectivo…" />
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
